import {
  type ClassificationScheme,
  classificationSchemeSchema,
  type DocumentList,
  MAX_UPLOAD_MB,
  type PrincipalOptions,
  type ProjectDetail,
  type ProjectGroupGrant,
  type ProjectMemberView,
  type ProjectRuleGrant,
  type ProjectSettingResponse,
  type ProjectSummary,
  type ProjectView,
} from "@esi/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { z } from "zod";
import { api } from "../api.ts";
import { ApiError, ok } from "../api-errors.ts";

/** Query keys of the project screens, so mutations invalidate exactly what they changed. */
export const projectKeys = {
  all: ["projects"] as const,
  project: (id: string) => ["projects", id] as const,
  members: (id: string) => ["projects", id, "members"] as const,
  /** Under `members`: a membership change refreshes who is already a member. */
  people: (id: string, query: string) => ["projects", id, "members", "search", query] as const,
  groups: (id: string) => ["projects", id, "members", "groups"] as const,
  ruleGrants: (id: string) => ["projects", id, "members", "rules"] as const,
  documents: (id: string) => ["projects", id, "documents"] as const,
  knowledge: (id: string) => ["projects", id, "knowledge-settings"] as const,
  settings: (id: string) => ["projects", id, "settings"] as const,
  summary: (id: string) => ["projects", id, "summary"] as const,
};

export function useProjects() {
  return useQuery({
    queryKey: projectKeys.all,
    queryFn: async (): Promise<ProjectView[]> => ok(await api.api.projects.$get()),
  });
}

export function useProject(projectId: string) {
  return useQuery({
    queryKey: projectKeys.project(projectId),
    queryFn: async (): Promise<ProjectDetail> =>
      ok(await api.api.projects[":projectId"].$get({ param: { projectId } })),
  });
}

/**
 * The project summary (spec `project-home` "Project summary read"): Overview's figures in one
 * bounded read. A 404 is not retried: an API that predates the route answers it, and the figure
 * sections then derive their figures from the lists ({@link summaryUnavailable}, one release).
 */
export function useProjectSummary(projectId: string) {
  return useQuery({
    queryKey: projectKeys.summary(projectId),
    queryFn: async (): Promise<ProjectSummary> =>
      ok(await api.api.projects[":projectId"].summary.$get({ param: { projectId } })),
    retry: (failures, error) => !summaryUnavailable(error) && failures < 3,
    // While files are processing, as the documents list polls them.
    refetchInterval: (query) => ((query.state.data?.sources.processing ?? 0) > 0 ? 5000 : false),
  });
}

/**
 * Mutations invalidate the lists they change (documents, the wiki index, members), not the
 * summary: when one of those lists refetches with new data after its first load, the summary
 * follows, so Overview's figures never lag the tabs. Mounted once, by Overview.
 */
export function useSummaryFollowsLists(projectId: string) {
  const client = useQueryClient();
  useEffect(
    () =>
      client.getQueryCache().subscribe((event) => {
        if (event.type !== "updated" || event.action.type !== "success") {
          return;
        }
        const [root, id, kind, sub] = event.query.queryKey as unknown[];
        const followed =
          kind === "documents" || kind === "members" || (kind === "wiki" && sub === "index");
        if (
          root === "projects" &&
          id === projectId &&
          followed &&
          event.query.state.dataUpdateCount > 1 &&
          // An API without the summary: the lists are the figures, nothing to follow.
          !summaryUnavailable(client.getQueryState(projectKeys.summary(projectId))?.error)
        ) {
          void client.invalidateQueries({ queryKey: projectKeys.summary(projectId) });
        }
      }),
    [client, projectId],
  );
}

/** The summary route is missing (an older API during a canary): fall back to the lists. */
export const summaryUnavailable = (error: unknown) =>
  error instanceof ApiError && error.status === 404;

export function useMembers(projectId: string, enabled = true) {
  return useQuery({
    queryKey: projectKeys.members(projectId),
    enabled,
    queryFn: async (): Promise<ProjectMemberView[]> =>
      ok(await api.api.projects[":projectId"].members.$get({ param: { projectId } })),
  });
}

/** Roles granted to groups, with member and excluded counts (never member names). */
export function useProjectGroups(projectId: string, enabled = true) {
  return useQuery({
    queryKey: projectKeys.groups(projectId),
    enabled,
    queryFn: async (): Promise<ProjectGroupGrant[]> =>
      ok(await api.api.projects[":projectId"].members.groups.$get({ param: { projectId } })),
  });
}

/** Roles granted to attribute rules, with the rule's name. */
export function useProjectRuleGrants(projectId: string, enabled = true) {
  return useQuery({
    queryKey: projectKeys.ruleGrants(projectId),
    enabled,
    queryFn: async (): Promise<ProjectRuleGrant[]> =>
      ok(await api.api.projects[":projectId"]["rule-grants"].$get({ param: { projectId } })),
  });
}

/** The tenant's groups and attribute rules a member may pick (names only). */
export function usePrincipals(enabled = true) {
  return useQuery({
    queryKey: ["principals"] as const,
    enabled,
    queryFn: async (): Promise<PrincipalOptions> => ok(await api.api.me.principals.$get()),
    staleTime: 60_000,
  });
}

/**
 * Documents still moving (`processing`) are polled until they settle. Fresh for a short while,
 * so Overview, the Sources tab and the knowledge tree share one list when switching between them
 * (writes invalidate it; polling is unaffected).
 */
export function useDocuments(projectId: string, enabled = true) {
  return useQuery({
    queryKey: projectKeys.documents(projectId),
    enabled,
    staleTime: 30_000,
    queryFn: async (): Promise<DocumentList> =>
      ok(await api.api.projects[":projectId"].documents.$get({ param: { projectId } })),
    refetchInterval: (query) =>
      query.state.data?.documents.some((d) => d.status === "processing") ? 3000 : false,
  });
}

/** What the project's knowledge screens need from its effective settings. */
export interface KnowledgeSettings {
  scheme: ClassificationScheme;
  defaultClassification: string;
  maxUploadMb: number;
  /** `knowledge.ocrMaxPages`: pages read by OCR per scan; null when the API does not say. */
  ocrMaxPages: number | null;
}

const resolvedSettingsSchema = z.array(z.object({ key: z.string(), value: z.unknown() }));

/** Reads the knowledge keys out of `GET /projects/:id/settings`; bad values fail loudly. */
export function knowledgeSettings(resolved: unknown): KnowledgeSettings {
  const values = new Map(resolvedSettingsSchema.parse(resolved).map((s) => [s.key, s.value]));
  const scheme = classificationSchemeSchema.parse(values.get("knowledge.classificationScheme"));
  const fallback = scheme[0]?.id ?? "";
  const defaultClassification = z
    .string()
    .catch(fallback)
    .parse(values.get("knowledge.defaultClassification"));
  return {
    scheme,
    defaultClassification: scheme.some((l) => l.id === defaultClassification)
      ? defaultClassification
      : fallback,
    maxUploadMb: z
      .int()
      .min(1)
      .max(MAX_UPLOAD_MB)
      .catch(MAX_UPLOAD_MB)
      .parse(values.get("knowledge.maxUploadMb")),
    ocrMaxPages: z
      .int()
      .min(1)
      .nullable()
      .catch(null)
      .parse(values.get("knowledge.ocrMaxPages") ?? null),
  };
}

/** Every setting of the project with its source, lock, overridability and inherited value. */
export function useProjectSettings(projectId: string, enabled = true) {
  return useQuery({
    queryKey: projectKeys.settings(projectId),
    enabled,
    queryFn: async (): Promise<ProjectSettingResponse[]> =>
      ok(await api.api.projects[":projectId"].settings.$get({ param: { projectId } })),
  });
}

/** Not for guests: the server keeps a project's settings from them (`project.view_members`). */
export function useKnowledgeSettings(projectId: string, enabled = true) {
  return useQuery({
    queryKey: projectKeys.knowledge(projectId),
    enabled,
    queryFn: async () =>
      knowledgeSettings(
        await ok(await api.api.projects[":projectId"].settings.$get({ param: { projectId } })),
      ),
  });
}
